import React, { createContext, useContext, useState, useRef, useEffect } from 'react';
import logger from "#/utils/logger";

interface PlaybackObjects {
  audioCtx: AudioContext;
  audioNode?: MediaElementAudioSourceNode;
  analyser?: AnalyserNode;
  audioElement: HTMLAudioElement;
}

interface AudioPlaybackContextType {
  playbackObjects: React.RefObject<PlaybackObjects | null>;
  initPlayback: (element: HTMLAudioElement) => void;
  isPlaying: boolean;
  isPaused: boolean;
  audioUnlocked: boolean;
  pausePlaying: () => void;
  resumePlaying: () => void;
  replayAudio: () => void;
  setAudioUnlocked: (audioUnlocked: boolean) => void;
}

const AudioPlaybackContext = createContext<AudioPlaybackContextType | undefined>(undefined);

export const AudioPlaybackProvider: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  const playbackObjects = useRef<PlaybackObjects | null>(null);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [isPaused, setIsPaused] = useState<boolean>(false);
  const [audioUnlocked, setAudioUnlocked] = useState<boolean>(false);
  const isIOSRef = useRef<boolean>(false);

  function createPlaybackObjects(element: HTMLAudioElement): PlaybackObjects {
    logger.info("Creating playback objects");

    const audioCtx = new AudioContext({
      latencyHint: 'playback'
    });

    // Initialize audionode
    let source: MediaElementAudioSourceNode | undefined = undefined;
    let analyser: AnalyserNode | undefined = undefined;
    if (isIOSRef.current) {
      // Fake analyser
      logger.debug("iOS playback detected.");
    }
    else {
      // Initialize Analyzer
      const sourceNode = audioCtx.createMediaElementSource(element);
      const analyserNode = audioCtx.createAnalyser();
      analyserNode.fftSize = 2048;
      analyserNode.smoothingTimeConstant = 0.8;

      // Connect elements
      sourceNode.connect(analyserNode);
      analyserNode.connect(audioCtx.destination);
      source = sourceNode;
      analyser = analyserNode;
    }

    if (audioCtx.state === 'suspended') {
      audioCtx.resume()
        .then(() => logger.debug("AudioContext resumed"))
        .catch((error) => logger.debug("AudioContext resuming failed", error));
    }

    // Connect events
    element.addEventListener("error", () => logger.error("Audio error detected"));
    element.addEventListener("playing", () => {
      logger.debug("Playback started");
      setIsPlaying(true);
      setIsPaused(false);
    });
    element.addEventListener("pause", () => {
      logger.debug("Playback paused");
      setIsPlaying(false);
      setIsPaused(true);
    });
    element.addEventListener("ended", () => {
      logger.debug("Playback ended");
      setIsPlaying(false);
      setIsPaused(false);
    });

    logger.info("Playback objects created");

    return {
      audioCtx: audioCtx,
      audioNode: source,
      analyser: analyser,
      audioElement: element
    };
  }

  useEffect(() => {
    isIOSRef.current = CSS.supports("-webkit-touch-callout: none");
    if (isIOSRef.current) {
      logger.info("iOS browser detected");
    }
  }, []);

  const initPlayback = (element: HTMLAudioElement) => {
    if (playbackObjects.current && playbackObjects.current.audioElement != element) {
      logger.info("Discarding current playback objects")
      playbackObjects.current.audioElement.pause();
      playbackObjects.current.analyser?.disconnect();
      playbackObjects.current = null;
    }
    if (!playbackObjects.current) {
      playbackObjects.current = createPlaybackObjects(element);
    }
  }

  const pausePlaying = () => {
    if (playbackObjects.current) {
      logger.debug("Pausing audio playback");
      const audio = playbackObjects.current.audioElement;
      audio.pause()
    }
    else {
      logger.debug("Cannot pause: playback objects not initialized");
    }
  }

  const resumePlaying = () => {
    if (playbackObjects.current && isPaused) {
      logger.debug("Resuming audio playback");
      const audio = playbackObjects.current.audioElement;
      audio.play()
    }
  }

  const replayAudio = () => {
    if (playbackObjects.current) {
      playbackObjects.current.audioElement.pause();
      playbackObjects.current.audioElement.currentTime = 0;
      playbackObjects.current.audioElement.play();
    }
  }

  return (
    <AudioPlaybackContext.Provider
      value={{
        playbackObjects,
        initPlayback,
        isPlaying,
        isPaused,
        audioUnlocked,
        pausePlaying,
        resumePlaying,
        replayAudio,
        setAudioUnlocked,
      }}
    >
      {children}
    </AudioPlaybackContext.Provider>
  );
};

export const useAudioPlayback = () => {
  const context = useContext(AudioPlaybackContext);
  if (context === undefined) {
    throw new Error('useAudioPlayback must be used within an AudioPlaybackProvider');
  }
  return context;
};
